'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, Bell, Search, ChevronDown, Sparkles, LogOut } from 'lucide-react';

interface TopbarProps {
  onToggleSidebar: () => void;
  onMobileMenu: () => void;
  role: 'student' | 'parent' | 'professional' | 'admin';
  campusMode: 'kid' | 'teen' | 'pro';
}

const modeLabels = { kid: '🌟 Kid Mode', teen: '🚀 Teen Mode', pro: '🎓 Pro Mode' };
const modeColors = {
  kid: 'bg-green-100 text-green-700 border-green-200',
  teen: 'bg-blue-100 text-blue-700 border-blue-200',
  pro: 'bg-primary/10 text-primary border-primary/20',
};

export default function Topbar({ onToggleSidebar, onMobileMenu, role, campusMode }: TopbarProps) {
  const [notifOpen, setNotifOpen] = useState(false);

  const notifications = [
    { id: 'notif-1', text: 'New lesson available: Intro to Coding', time: '2m ago', dot: 'bg-primary' },
    { id: 'notif-2', text: 'Badge earned: Curious Explorer!', time: '1h ago', dot: 'bg-accent' },
    { id: 'notif-3', text: 'Parent approved Advanced Math access', time: '3h ago', dot: 'bg-secondary' },
  ];

  return (
    <header className="h-14 bg-card border-b border-border flex items-center px-4 gap-4 shrink-0 z-20">
      {/* Sidebar toggle — desktop */}
      <button
        onClick={onToggleSidebar}
        className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-colors"
        aria-label="Toggle sidebar"
      >
        <Menu size={18} className="text-muted-foreground" />
      </button>

      {/* Mobile menu */}
      <button
        onClick={onMobileMenu}
        className="flex lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
        aria-label="Open menu"
      >
        <Menu size={18} className="text-muted-foreground" />
      </button>

      {/* Logo (mobile only) */}
      <div className="flex lg:hidden items-center gap-2">
        <AppLogo size={28} />
        <span className="font-bold text-sm text-foreground">JarvondisU</span>
      </div>

      {/* Search */}
      <div className="hidden md:flex flex-1 max-w-sm items-center gap-2 bg-input rounded-lg px-3 py-2">
        <Search size={15} className="text-muted-foreground shrink-0" />
        <input
          type="text"
          placeholder="Search courses, lessons..."
          className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none flex-1"
        />
        <span className="text-xs text-muted-foreground border border-border rounded px-1">⌘K</span>
      </div>

      <div className="flex-1" />

      {/* Campus mode badge */}
      <span className={`hidden sm:flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${modeColors[campusMode]}`}>
        <Sparkles size={12} />
        {modeLabels[campusMode]}
      </span>

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={() => setNotifOpen((p) => !p)}
          className="relative p-2 rounded-lg hover:bg-muted transition-colors"
          aria-label="Notifications"
        >
          <Bell size={18} className="text-muted-foreground" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full pulse-dot" />
        </button>

        {notifOpen && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-modal z-50 fade-in">
            <div className="p-4 border-b border-border">
              <p className="font-semibold text-sm text-foreground">Notifications</p>
            </div>
            {notifications.map((n) => (
              <div key={n.id} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer">
                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${n.dot}`} />
                <div>
                  <p className="text-sm text-foreground">{n.text}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.time}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User */}
      <div className="flex items-center gap-2 cursor-pointer group">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-xs font-bold text-primary">
            {role === 'student' ? 'AO' : role === 'parent' ? 'DO' : 'NV'}
          </span>
        </div>
        <div className="hidden sm:block">
          <p className="text-sm font-semibold text-foreground leading-tight">
            {role === 'student' ? 'Amara' : role === 'parent' ? 'David' : 'Dr. Nadia'}
          </p>
          <p className="text-xs text-muted-foreground capitalize">{role}</p>
        </div>
        <ChevronDown size={14} className="text-muted-foreground group-hover:text-foreground transition-colors" />
      </div>

      {/* Logout */}
      <Link href="/sign-up-login-screen" className="p-2 rounded-lg hover:bg-muted transition-colors" title="Sign out">
        <LogOut size={16} className="text-muted-foreground" />
      </Link>
    </header>
  );
}